import { fetchSharedResourceBytes } from '@/features/published-project/api'
import {
  PROJECT_CURRENT_DATA_DIR,
  PROJECT_CURRENT_DIR,
  PROJECT_CURRENT_PASSTHROUGH_MAP,
  PROJECT_CURRENT_PROJECT_BIN,
} from '@/features/project-cloud/ossPaths'
import { findMapEntry, type PublishedMapEntry } from '@/features/published-project/mapManifest'
import BrowserUtil from '@/platform/oss/browserUtil'

export type PublishedBinCacheItem = {
  relativePath: string
  objectKey: string
  bytes: Uint8Array
}

export type PublishedPassthroughMapItem = PublishedMapEntry

const PUBLISHED_PASSTHROUGH_ENTRY_PREFIXES = [
  'data/lmcode/',
  'logic/',
  'script/',
] as const
const PUBLISHED_PASSTHROUGH_ENTRY_PATHS = new Set([
  'data/mall.bin',
  'data/malllimite.bin',
  'data/welfare.bin',
  'data/name.bin',
  'data/dress.bin',
])

type PublishedBinCacheTarget = {
  relativePath: string
  objectKey: string
}

function toBlob(bytes: Uint8Array): Blob {
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
  return new Blob([buffer], { type: 'application/octet-stream' })
}

function normalizePath(value: string): string {
  return String(value || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .trim()
}

function isPublishedPassthroughEntry(entry: PublishedMapEntry): boolean {
  const path = normalizePath(entry.relativePath).toLowerCase()
  return PUBLISHED_PASSTHROUGH_ENTRY_PATHS.has(path)
    || PUBLISHED_PASSTHROUGH_ENTRY_PREFIXES.some((prefix) => path.startsWith(prefix))
}

async function readBinFromMap(entries: PublishedMapEntry[], relativePath: string): Promise<Uint8Array | null> {
  const entry = findMapEntry(entries, relativePath)
  return entry ? await fetchSharedResourceBytes(entry.md5) : null
}

async function readRequiredBinFromMap(params: {
  entries: PublishedMapEntry[]
  relativePath: string
}): Promise<Uint8Array> {
  const fromMap = await readBinFromMap(params.entries, params.relativePath)
  if (fromMap) return fromMap
  throw new Error(`发布清单缺少 ${params.relativePath}，无法打开该版本`)
}

/** 收集已发布版本中需要写回 current 的核心工程文件。 */
export async function collectPublishedBinsForCache(params: {
  guid: string
  version: string
  sessionId: string
  toolVersion: string
  entries: PublishedMapEntry[]
  projectBinBytes: Uint8Array
}): Promise<PublishedBinCacheItem[]> {
  const out: PublishedBinCacheItem[] = [
    {
      relativePath: 'Data/ProjectS.bin',
      objectKey: `${params.guid}/${PROJECT_CURRENT_PROJECT_BIN}`,
      bytes: params.projectBinBytes,
    },
  ]

  const requiredBins: PublishedBinCacheTarget[] = [
    { relativePath: 'Data/MallNew.bin', objectKey: `${params.guid}/${PROJECT_CURRENT_DATA_DIR}/MallNew.bin` },
  ]
  for (const item of requiredBins) {
    out.push({
      relativePath: item.relativePath,
      objectKey: item.objectKey,
      bytes: await readRequiredBinFromMap({ ...params, ...item }),
    })
  }

  return out
}

/** 收集轻量版不编辑的 3.0 文件清单，只保留 Map 条目用于后续发布透传。 */
export function collectPublishedPassthroughMap(entries: PublishedMapEntry[]): PublishedPassthroughMapItem[] {
  const out: PublishedPassthroughMapItem[] = []
  const seen = new Set<string>()
  for (const entry of entries) {
    if (!isPublishedPassthroughEntry(entry)) continue
    const relativePath = normalizePath(entry.relativePath)
    const key = relativePath.toLowerCase()
    if (!relativePath || seen.has(key)) continue
    seen.add(key)
    out.push({
      relativePath,
      size: entry.size,
      md5: entry.md5,
    })
  }
  return out
}

/** 把 3.0 透传文件的 Map 条目写入当前工程 OSS，不下载文件内容。 */
export async function cachePublishedPassthroughMapToOss(params: {
  guid: string
  uid: string | number
  entries: PublishedPassthroughMapItem[]
}): Promise<void> {
  const oss = BrowserUtil.getInstance().connect(params.uid)
  await oss.createFolder(params.guid)
  await oss.createFolder(`${params.guid}/${PROJECT_CURRENT_DIR}`)
  await oss.writeData(
    `${params.guid}/${PROJECT_CURRENT_PASSTHROUGH_MAP}`,
    JSON.stringify(params.entries, null, 2),
    { contentType: 'application/json;charset=utf-8' },
  )
}

/** 把服务器发布版本中的核心 bin 缓存到当前用户 OSS 云盘。 */
export async function cachePublishedBinsToOss(params: {
  guid: string
  uid: string | number
  bins: PublishedBinCacheItem[]
  onProgress?: (current: number, total: number, fileName: string) => void
}): Promise<void> {
  const oss = BrowserUtil.getInstance().connect(params.uid)
  await oss.createFolder(params.guid)
  await oss.createFolder(`${params.guid}/${PROJECT_CURRENT_DIR}`)
  await oss.createFolder(`${params.guid}/${PROJECT_CURRENT_DATA_DIR}`)
  await oss.createFolder(`${params.guid}/${PROJECT_CURRENT_DIR}/other`)
  for (let index = 0; index < params.bins.length; index += 1) {
    const item = params.bins[index]!
    params.onProgress?.(index + 1, params.bins.length, item.objectKey)
    await oss.uploadFile(toBlob(item.bytes), {
      objectKey: item.objectKey,
      fileName: item.objectKey.split('/').pop() || 'bin',
    })
  }
}
